// Smart Sash Admin Panel - Device Registration Page
// Session validation → login form → lab selection → gRPC activation
// Route: /device-registration?sessionId=...

import { DeviceRegistrationForm } from '@/components/auth/DeviceRegistrationForm';
import { LabSelection } from '@/components/auth/LabSelection';
import { MissingParametersError } from '@/components/auth/MissingParametersError';
import { SessionExpiredError } from '@/components/auth/SessionExpiredError';
import { SessionInvalidError } from '@/components/auth/SessionInvalidError';
import { Spinner } from '@/components/common';
import {
  AbortedScreen,
  CancelledScreen,
  ClosedScreen,
  ErrorScreen,
  ExpiredScreen,
  LoadingScreen,
  SuccessScreen,
  WaitingScreen,
} from '@/components/rfid-activation';
import { useDeviceRegistration } from '@/hooks/useDeviceRegistration';
import React from 'react';

const COPYRIGHT = `© ${new Date().getFullYear()} H. H. Hawkins Ltd.`;

/**
 * Device Registration Page
 *
 * Flow:
 *  1. Validate session params from URL query string
 *  2. Show login form → user authenticates
 *  3. Show lab selection dropdown
 *  4. Subscribe to gRPC and request device registration
 *  5. Display verification code + countdown
 *  6. On success → congratulations screen
 *  7. On cancel/error → show terminal screen
 */
export const DeviceRegistration: React.FC = () => {
  const {
    pageStage,
    sessionErrorType,
    sessionErrorMessage,
    activationStatus,
    verificationCode,
    verificationType,
    timeRemaining,
    activationErrorMessage,
    handleFormSuccess,
    handleLabSelect,
    handleCancel,
    handleStartOver,
    handleGoToLogin,
  } = useDeviceRegistration();

  // ── Session validating ──────────────────────────────────────────────────
  if (pageStage === 'validating') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <Spinner />
          <p className="mt-3 text-muted">Validating session...</p>
        </div>
      </div>
    );
  }

  // ── Session error ───────────────────────────────────────────────────────
  if (pageStage === 'session-error') {
    if (sessionErrorType === 'missing-params') {
      return <MissingParametersError errorMessage={sessionErrorMessage || undefined} />;
    }
    if (sessionErrorType === 'expired') {
      return <SessionExpiredError errorMessage={sessionErrorMessage || undefined} />;
    }
    return <SessionInvalidError errorMessage={sessionErrorMessage || undefined} />;
  }

  // ── Session closed ──────────────────────────────────────────────────────
  if (pageStage === 'closed') {
    return <ClosedScreen onGoToLogin={handleGoToLogin} />;
  }

  // ── Login form ──────────────────────────────────────────────────────────
  if (pageStage === 'form') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  <div className="text-center mb-4">
                    <h1 className="h3 mb-3 fw-bold">Device Registration</h1>
                    <p className="text-muted">Sign in to continue</p>
                  </div>
                  <DeviceRegistrationForm onSuccess={handleFormSuccess} />
                  <div className="text-center mt-4 pt-4 border-top">
                    <p className="text-muted small mb-0">{COPYRIGHT}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Lab selection ───────────────────────────────────────────────────────
  if (pageStage === 'lab-selection') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-lg border-0">
                <div className="card-body p-5">
                  <div className="text-center mb-4">
                    <h1 className="h3 mb-3 fw-bold">Select Lab</h1>
                    <p className="text-muted">Choose the lab for device registration</p>
                  </div>
                  <LabSelection onSelect={handleLabSelect} />
                  <div className="text-center mt-4 pt-4 border-top">
                    <p className="text-muted small mb-0">{COPYRIGHT}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Activation phase ────────────────────────────────────────────────────
  if (activationStatus === 'loading') {
    return <LoadingScreen message="Connecting to device registration service..." />;
  }
  if (activationStatus === 'success') {
    return (
      <SuccessScreen
        onGoToLogin={handleGoToLogin}
        title="Device Registered!"
        message="Your device has been registered successfully."
        alertMessage="The device is now active and ready to use."
      />
    );
  }
  if (activationStatus === 'expired') {
    return (
      <ExpiredScreen
        onStartOver={handleStartOver}
        title="Registration Expired"
        message="Your registration code has expired. Please start the process again."
      />
    );
  }
  if (activationStatus === 'error') {
    return (
      <ErrorScreen
        errorMessage={activationErrorMessage}
        onStartOver={handleStartOver}
        title="Registration Error"
      />
    );
  }
  if (activationStatus === 'aborted') {
    return (
      <AbortedScreen
        onStartOver={handleStartOver}
        title="Registration Aborted"
        message="The device registration has been cancelled and the request was revoked."
      />
    );
  }
  if (activationStatus === 'cancelled') {
    return (
      <CancelledScreen
        reason={activationErrorMessage}
        title="Registration Cancelled"
        defaultReason="The device registration was cancelled."
      />
    );
  }

  // waiting — show verification code
  return (
    <WaitingScreen
      verificationCode={verificationCode}
      verificationType={verificationType}
      timeRemaining={timeRemaining}
      onCancel={handleCancel}
      title="Device Registration"
      subtitle="Enter this code on your device"
      hint="Enter this code on your device screen to complete registration."
    />
  );
};

export default DeviceRegistration;
